/**
 * Utility functions for handling notifications
 */

/**
 * Handle notification click - navigate to appropriate page based on notification type and link
 */
export const handleNotificationClick = (notification, navigate) => {
  if (!notification) return;

  // Prefer formatted_link (which includes query params) over link_url
  const link = notification.formatted_link || notification.link_url;
  if (!link) return;

  // If it's a full URL, use it directly
  if (link.startsWith('http://') || link.startsWith('https://')) {
    window.location.href = link;
    return;
  }

  // If it's an API path, convert to frontend path
  if (link.startsWith('/api/')) {
    // Handle specific API path conversions
    if (link.includes('/teams/teams/')) {
      // Extract team ID from URL (handle both with and without query params)
      const teamIdMatch = link.match(/\/teams\/teams\/(\d+)/);
      if (teamIdMatch) {
        const teamId = teamIdMatch[1];
        // Get tab from link_params or extract from URL query string
        let tab = notification.link_params?.tab || '';
        if (!tab && link.includes('?')) {
          const urlParams = new URLSearchParams(link.split('?')[1]);
          tab = urlParams.get('tab') || '';
        }
        // Use query parameter instead of hash for tab
        const frontendPath = `/teams/${teamId}${tab ? `?tab=${tab}` : ''}`;
        navigate(frontendPath);
        return;
      }
    }

    // Default: convert API paths to frontend paths
    // Remove /api/ prefix and handle query params
    let frontendPath = link.replace('/api/', '/');
    // Keep query params if they exist
    navigate(frontendPath);
    return;
  }

  // If it's a relative path, use React Router navigation
  if (link.startsWith('/')) {
    // Extract scroll_to from query params or link_params
    let scrollToId = null;
    let pathToNavigate = link;

    // Check if scroll_to is in the URL query string
    if (link.includes('?')) {
      const [path, queryString] = link.split('?');
      const urlParams = new URLSearchParams(queryString);
      scrollToId = urlParams.get('scroll_to');
      // Remove scroll_to from query params for navigation (it's not a route param)
      urlParams.delete('scroll_to');
      const remainingParams = urlParams.toString();
      pathToNavigate = remainingParams ? `${path}?${remainingParams}` : path;
    }

    // Fallback to link_params if not in URL
    if (!scrollToId && notification.link_params?.scroll_to) {
      scrollToId = notification.link_params.scroll_to;
    }

    // Navigate to the path (without scroll_to in query)
    navigate(pathToNavigate);

    // Scroll to element after navigation if specified
    if (scrollToId) {
      // Use setTimeout to ensure navigation completes first
      setTimeout(() => {
        const element = document.getElementById(scrollToId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300); // Increased timeout to ensure page has rendered
    }
    return;
  }

  // Default: try to navigate to the link as-is
  navigate(link);
};
